import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
});

// 添加请求拦截器，自动添加Authorization头
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const currentTenant = localStorage.getItem('currentTenant') || 'default';
    config.headers['X-Tenant-Code'] = currentTenant;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 工作流消息
export interface AgentWrokflowMessage {
  id?: number;
  tenant_code: string;
  message_id: string;
  source_channel: 'email' | 'whatsapp' | 'wechat' | 'api' | 'webhook' | 'sms';
  channel_message_id?: string;
  sender_identifier?: string;
  receiver_identifier?: string;
  subject?: string;
  message_type?: 'text' | 'image' | 'video' | 'file' | 'audio' | 'mixed';
  message_content?: string;
  message_data?: any;
  attachments_info?: Array<{
    name: string;
    size: number;
    type: string;
    mime_type?: string;
    storage_path?: string;
    url?: string;
  }>;
  metadata?: any;
  storage_type?: 'database' | 'file' | 'object_storage';
  storage_path?: string;
  customer_code?: string;
  is_processed: boolean;
  received_at?: string;
  created_at?: string;
  updated_at?: string;
}

// 工作流执行
export interface AgentWrokflowExecution {
  id?: number;
  tenant_code: string;
  execution_id: string;
  message_id?: string;
  source_channel?: 'email' | 'whatsapp' | 'wechat' | 'api' | 'webhook' | 'sms';
  workflow_name: string;
  workflow_type?: 'order_creation' | 'notification' | 'custom';
  status: 'pending' | 'running' | 'success' | 'failed' | 'retrying' | 'manual_review';
  current_node?: string;
  retry_count: number;
  max_retries: number;
  error_message?: string;
  error_code?: string;
  execution_result?: any;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

// 工作流节点
export interface AgentWrokflowNode {
  id?: number;
  execution_id: string;
  node_name: string;
  node_type: 'message_parse' | 'api_call' | 'order_create' | 'data_transform' | 'condition' | 'parallel';
  node_order: number;
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  input_data?: any;
  output_data?: any;
  error_message?: string;
  error_code?: string;
  retry_count: number;
  execution_time_ms?: number;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

// 工作流重试
export interface AgentWrokflowRetry {
  id?: number;
  execution_id: string;
  retry_number: number;
  retry_reason: 'timeout' | '500_error' | 'network_error' | 'rate_limit';
  retry_at: string;
  retry_result: 'success' | 'failed';
  error_message?: string;
  error_code?: string;
  created_at?: string;
}

// 人工审核
export interface AgentWrokflowManualReview {
  id?: number;
  execution_id: string;
  message_id?: string;
  source_channel?: 'email' | 'whatsapp' | 'wechat' | 'api' | 'webhook' | 'sms';
  review_status: 'pending' | 'reviewing' | 'resolved' | 'rejected';
  review_reason: 'missing_customer' | 'invalid_params' | 'api_failed' | 'parse_error' | 'timeout' | 'unknown';
  reviewer_user_id?: number;
  review_notes?: string;
  resolved_at?: string;
  resolution_action?: 'retry' | 'ignore' | 'cancel';
  resolution_data?: any;
  created_at?: string;
  updated_at?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const agentWrokflowService = {
  // ==================== 消息相关 ====================
  
  async queryMessages(params: {
    tenant_code: string;
    filters?: {
      source_channel?: string;
      is_processed?: boolean;
      start_date?: string;
      end_date?: string;
    };
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AgentWrokflowMessage>> {
    const response = await api.post('/agent/workflow/messages/query', params);
    return response.data.data;
  },

  async getMessage(messageId: string): Promise<AgentWrokflowMessage> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/agent/workflow/messages/${messageId}`, {
      params: { tenant_code: tenantCode }
    });
    return response.data.data;
  },

  async updateMessage(messageId: string, updates: Partial<AgentWrokflowMessage>): Promise<void> {
    await api.put(`/agent/workflow/messages/${messageId}`, {
      tenant_code: localStorage.getItem('currentTenant') || 'default',
      ...updates
    });
  },

  async deleteMessage(messageId: string): Promise<void> {
    await api.delete(`/agent/workflow/messages/${messageId}`, {
      data: {
        tenant_code: localStorage.getItem('currentTenant') || 'default'
      }
    });
  },

  async executeWorkflow(messageId: string): Promise<{ execution_id: string; workflow_name?: string; agent_user_id?: string }> {
    const response = await api.post('/agent/workflow/execute', {
      tenant_code: localStorage.getItem('currentTenant') || 'default',
      message_id: messageId
    });
    return response.data.data;
  },

  // ==================== 执行相关 ====================
  
  async queryExecutions(params: {
    tenant_code: string;
    filters?: {
      status?: string;
      source_channel?: string;
      workflow_name?: string;
      start_date?: string;
      end_date?: string;
    };
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AgentWrokflowExecution>> {
    const response = await api.post('/agent/workflow/executions/query', params);
    return response.data.data;
  },

  async getExecution(executionId: string): Promise<AgentWrokflowExecution> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/agent/workflow/executions/${executionId}`, {
      params: {
        tenant_code: tenantCode
      }
    });
    return response.data.data.execution;
  },

  async updateExecution(executionId: string, updates: Partial<AgentWrokflowExecution>): Promise<void> {
    await api.put(`/agent/workflow/executions/${executionId}`, {
      tenant_code: localStorage.getItem('currentTenant') || 'default',
      ...updates
    });
  },

  async deleteExecution(executionId: string): Promise<void> {
    await api.delete(`/agent/workflow/executions/${executionId}`, {
      data: {
        tenant_code: localStorage.getItem('currentTenant') || 'default'
      }
    });
  },

  // ==================== 节点相关 ====================
  
  async getNodes(executionId: string): Promise<AgentWrokflowNode[]> {
    const response = await api.get(`/agent/workflow/executions/${executionId}`, {
      params: {
        tenant_code: localStorage.getItem('currentTenant') || 'default'
      }
    });
    return response.data.data.nodes || [];
  },

  async updateNode(nodeId: number, updates: Partial<AgentWrokflowNode>): Promise<void> {
    await api.put(`/agent/workflow/nodes/${nodeId}`, {
      tenant_code: localStorage.getItem('currentTenant') || 'default',
      ...updates
    });
  },

  async deleteNode(nodeId: number): Promise<void> {
    await api.delete(`/agent/workflow/nodes/${nodeId}`, {
      data: {
        tenant_code: localStorage.getItem('currentTenant') || 'default'
      }
    });
  },

  // ==================== 重试相关 ====================
  
  async getRetries(executionId: string): Promise<AgentWrokflowRetry[]> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    const response = await api.get(`/agent/workflow/executions/${executionId}`, {
      params: {
        tenant_code: tenantCode
      }
    });
    // 从执行详情中获取重试记录
    return response.data.data.retries || [];
  },

  // ==================== 人工审核相关 ====================
  
  async queryManualReviews(params: {
    tenant_code: string;
    filters?: {
      review_status?: string;
      source_channel?: string;
      review_reason?: string;
    };
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AgentWrokflowManualReview>> {
    const response = await api.post('/agent/workflow/manual-reviews/query', params);
    return response.data.data;
  },

  async getManualReview(reviewId: number): Promise<AgentWrokflowManualReview> {
    const response = await api.get(`/agent/workflow/manual-reviews/${reviewId}`);
    return response.data.data;
  },

  async updateManualReview(reviewId: number, updates: Partial<AgentWrokflowManualReview>): Promise<void> {
    await api.put(`/agent/workflow/manual-reviews/${reviewId}`, {
      tenant_code: localStorage.getItem('currentTenant') || 'default',
      ...updates
    });
  },

  async deleteManualReview(reviewId: number): Promise<void> {
    await api.delete(`/agent/workflow/manual-reviews/${reviewId}`, {
      data: {
        tenant_code: localStorage.getItem('currentTenant') || 'default'
      }
    });
  },
};

